import { useState } from 'react';
import { useMutation, useApolloClient, gql } from '@apollo/client';
import { CREATE_REVIEW_SECURE } from '../../graphql/mutations/reviewsMutations';
import { trackReviewSubmitted } from '../../util/tracking';
import {
  createAlreadyReviewedError,
  normalizeReviewError,
} from './reviewErrorUtils';

const GET_BOOKING_DETAILS = gql`
  query GetBooking($id: ID!) {
    getBooking(id: $id) {
      id
      guestReviewed
      hostReviewed
      reviews {
        items {
          id
          reviewerId
          revieweeId
          reviewType
        }
      }
    }
  }
`;

export const useCreateUserReviews = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const client = useApolloClient();

  const [createReviewSecureMutation] = useMutation(CREATE_REVIEW_SECURE);

  const checkIfAlreadyReviewed = async (reviewData) => {
    const { data } = await client.query({
      query: GET_BOOKING_DETAILS,
      variables: { id: reviewData.bookingId },
      fetchPolicy: 'network-only'
    });

    if (!data?.getBooking) {
      return false;
    }

    const { reviews, hostReviewed } = data.getBooking;

    if (hostReviewed) {
      return true;
    }

    // Check if there's already a GUEST_REVIEW for this booking
    const hasReviewed = reviews?.items?.some(
      review => review?.reviewType === "GUEST_REVIEW"
    );

    return hasReviewed;
  };

  const invalidateRelevantCaches = async ({ bookingId, userId }) => {
    try {
      if (bookingId) {
        await client.query({
          query: GET_BOOKING_DETAILS,
          variables: { id: bookingId },
          fetchPolicy: 'network-only'
        });
      }
      
      await client.reFetchObservableQueries();
    } catch (error) {
      console.warn("Cache invalidation had some issues:", error);
    }
  };

  const submitUserReview = async (reviewData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const alreadyReviewed = await checkIfAlreadyReviewed(reviewData);

      if (alreadyReviewed) {
        throw createAlreadyReviewedError();
      }

      if (!reviewData?.bookingId || !reviewData?.rating) {
        throw new Error('Missing required fields');
      }

      // Secure backend creates the review and marks the booking atomically.
      const { data: reviewResult } = await createReviewSecureMutation({
        variables: {
          input: {
            bookingID: reviewData.bookingId,
            rating: reviewData.rating,
            content: reviewData.content || "",
            communicationRating: reviewData.communicationRating,
            reviewType: "GUEST_REVIEW"
          }
        }
      });

      // Invalidate relevant caches
      await invalidateRelevantCaches({
        bookingId: reviewData.bookingId,
        userId: reviewData.revieweeId
      });

      trackReviewSubmitted({
        reviewType: 'guest_review',
        rating: reviewData.rating,
        source: 'booking_details'
      });

      return reviewResult?.markBookingReviewed?.review;
    } catch (error) {
      const normalizedError = normalizeReviewError(error);
      console.error('Error in submitUserReview:', normalizedError);
      setError(normalizedError);
      throw normalizedError;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitUserReview,
    isSubmitting,
    error,
    invalidateRelevantCaches,
    checkIfAlreadyReviewed
  };
};
